import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import './App.css';
import DiscoverAgents from './components/DiscoverAgents';
import ContextUpload from './components/ContextUpload';
import ChatInterface from './components/ChatInterface';
import Settings from './components/Settings';
import EmbedAgent from './components/EmbedAgent';
import ConversationHistory from './components/ConversationHistory';
import ImproveAgent from './components/ImproveAgent';
import AgentSettings from './components/AgentSettings';
import TuneYourAgent from './components/TuneYourAgent';
import WebsiteScanner from './components/WebsiteScanner';
import SimpleChat from './components/SimpleChat';
import ThemePreview from './components/ThemePreview';
import BuildChatMockup from './components/BuildChatMockup';
import ViewConversationsMockup from './components/ViewConversationsMockup';
import LandingPage from './components/LandingPage';
import ReactAdminPrototype from './components/ReactAdminPrototype';
import PublicChatInterface from './components/PublicChatInterface';
import Pricing from './components/Pricing';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancel from './components/PaymentCancel';
import Wallet from './components/Wallet';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import { useAuth } from './contexts/AuthContext';
import { StripeProvider } from './contexts/StripeContext';
import { WalletProvider } from './contexts/WalletContext';

// Public Conversation View Component
const PublicConversationView = () => {
  const { conversationId } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConversation = async () => {
      try {
        const response = await fetch(`/api/agents/public/conversations/${conversationId}`);
        if (response.ok) {
          const data = await response.json();
          const conversation = data.conversation;
          if (conversation) {
            // Get agent details
            const agentResponse = await fetch(`/api/agents/public`);
            if (agentResponse.ok) {
              const agentData = await agentResponse.json();
              const agentInfo = agentData.agents?.find(a => a.id === conversation.agent_id);
              if (agentInfo) {
                setAgent({
                  id: agentInfo.id,
                  name: agentInfo.name,
                  agent_avatar: agentInfo.agent_avatar,
                  creator_name: agentInfo.creator_name,
                  creator_picture: agentInfo.creator_picture
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
      } finally {
        setLoading(false);
      }
    };

    if (conversationId) {
      loadConversation();
    }
  }, [conversationId]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#1a1a1a',
        color: 'white'
      }}>
        Loading conversation...
      </div>
    );
  }

  if (!agent) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#1a1a1a',
        color: 'white'
      }}>
        Conversation not found
      </div>
    );
  }

  return (
    <div style={{ 
      height: '100vh', 
      background: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <PublicChatInterface 
        agent={agent} 
        conversationId={conversationId}
        onBack={() => window.history.back()}
        onUpdateUrl={() => {}} // No-op for public conversation view
      />
    </div>
  );
};

// Main App component with Router
function App() {
  const [webpages, setWebpages] = useState(() => {
    const saved = localStorage.getItem('webpages');
    return saved ? JSON.parse(saved) : [];
  });
  const [contextData, setContextData] = useState('');
  const [contextLoading, setContextLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { user, isAuthenticated } = useAuth();

  // Save webpages to localStorage when they change
  useEffect(() => {
    localStorage.setItem('webpages', JSON.stringify(webpages));
  }, [webpages]);

  // Load user's agent data when component mounts
  useEffect(() => {
    const loadUserAgent = async () => {
      if (!user || !isAuthenticated) {
        setContextLoading(false);
        return;
      }
      
      setContextLoading(true);
      
      // Check if user already has agent data from AuthContext
      if (user.agent?.builtInContext) {
        console.log('User already has agent data from AuthContext:', user.agent.builtInContext);
        setContextData(user.agent.builtInContext);
        // Don't return early - we still need to load files
      }
      
      try {
        const response = await fetch('/api/agents', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        console.log('loadUserAgent response status:', response.status);
        console.log('loadUserAgent response headers:', response.headers);
        
        if (response.ok) {
          const data = await response.json();
          console.log('loadUserAgent data:', data);
          if (data.agent?.builtInContext) {
            console.log('Setting context data:', data.agent.builtInContext);
            setContextData(data.agent.builtInContext);
          } else {
            console.log('No builtInContext found in agent data');
          }
        } else {
          console.log('loadUserAgent failed with status:', response.status);
        }
      } catch (error) {
        console.error('Error loading user agent:', error);
      }

      // Load uploaded files
      try {
        const filesResponse = await fetch('/api/agents/files', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (filesResponse.ok) {
          const filesData = await filesResponse.json();
          console.log('Loaded files in App:', filesData);
          if (filesData.files && Array.isArray(filesData.files)) {
            setUploadedFiles(filesData.files);
          }
        } else {
          console.log('Failed to load files:', filesResponse.status);
        }
      } catch (error) {
        console.error('Error loading files:', error);
      } finally {
        setContextLoading(false);
      }
    };

    loadUserAgent();
  }, [user, isAuthenticated]);

  return (
    <Router>
      <Routes>
        {/* Test page route */}
        <Route path="/test" element={<SimpleChat />} />
        
        {/* Theme preview route */}
        <Route path="/theme-preview" element={<ThemePreview />} />
        <Route path="/landing" element={<LandingPage />} />

        {/* Build chat mockup routes */}
        <Route path="/build-chat-mockup" element={<BuildChatMockup />} />
        <Route path="/view-conversations-mockup" element={<ViewConversationsMockup />} />
        
        {/* React Admin Prototype - Main App */}
        <Route path="/react-admin-prototype" element={<ReactAdminPrototype />} />
        
        {/* Build mode routes - Now using React Admin */}
        <Route path="/build" element={<ReactAdminPrototype><ChatInterface contextData={contextData} contextLoading={contextLoading} uploadedFiles={uploadedFiles} webpages={webpages} /></ReactAdminPrototype>} />
        <Route path="/build/upload" element={<ReactAdminPrototype><ContextUpload showFilesOnly={true} contextData={contextData} setContextData={setContextData} onFilesChange={setUploadedFiles} /></ReactAdminPrototype>} />
        <Route path="/build/text" element={<ReactAdminPrototype><ContextUpload showTextOnly={true} contextData={contextData} setContextData={setContextData} onFilesChange={setUploadedFiles} /></ReactAdminPrototype>} />
        <Route path="/build/websites" element={<ReactAdminPrototype><WebsiteScanner webpages={webpages} setWebpages={setWebpages} /></ReactAdminPrototype>} />
        <Route path="/build/tune" element={<ReactAdminPrototype><TuneYourAgent /></ReactAdminPrototype>} />
        <Route path="/build/improve" element={<ReactAdminPrototype><ImproveAgent /></ReactAdminPrototype>} />
        <Route path="/build/embed" element={<ReactAdminPrototype><EmbedAgent /></ReactAdminPrototype>} />
        <Route path="/build/conversations" element={<ReactAdminPrototype><ConversationHistory /></ReactAdminPrototype>} />
        <Route path="/build/agentsettings" element={<ReactAdminPrototype><AgentSettings /></ReactAdminPrototype>} />
        <Route path="/build/settings" element={<ReactAdminPrototype><Settings /></ReactAdminPrototype>} />
        
        {/* Conversation-specific routes (with UUIDs) */}
        <Route path="/build/c/:conversationId" element={<ReactAdminPrototype><ChatInterface contextData={contextData} contextLoading={contextLoading} uploadedFiles={uploadedFiles} webpages={webpages} /></ReactAdminPrototype>} />
        <Route path="/build/view/:conversationId" element={<ReactAdminPrototype><ConversationHistory /></ReactAdminPrototype>} />
        
        {/* Discover mode routes */}
        <Route path="/talkto" element={<ReactAdminPrototype><DiscoverAgents /></ReactAdminPrototype>} />
        
        {/* Public conversation route */}
        <Route path="/public/conversation/:conversationId" element={<PublicConversationView />} />
        
        {/* Settings route */}
        <Route path="/settings" element={<ReactAdminPrototype><Settings /></ReactAdminPrototype>} />
        
        {/* Payment routes */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/wallet" element={<ReactAdminPrototype><Wallet /></ReactAdminPrototype>} />
        
        {/* Root page - always show landing page */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Blog routes */}
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/build" replace />} />
      </Routes>
    </Router>
  );
}

// Wrap App with StripeProvider
const AppWithProviders = () => {
  return (
    <StripeProvider>
      <WalletProvider>
        <App />
        <Analytics />
      </WalletProvider>
    </StripeProvider>
  );
};

export default AppWithProviders;
